import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { fetchCampaignSales } from '@/lib/campaign-reports'
import type { CampaignSalesResponse } from '@/types/campaign-report'

interface UseCampaignSalesOptions {
  campaignId: number | null
  page: number
  perPage: number
  dateFrom?: string | null
  dateTo?: string | null
  enabled?: boolean
}

export function useCampaignSales({
  campaignId,
  page,
  perPage,
  dateFrom,
  dateTo,
  enabled = true,
}: UseCampaignSalesOptions) {
  const { token } = useAuth()
  const [data, setData] = useState<CampaignSalesResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!enabled) {
      setData(null)
      setIsLoading(false)
      setError(null)
      return
    }

    if (!campaignId) {
      setData(null)
      setIsLoading(false)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetchCampaignSales(campaignId, { page, perPage, dateFrom, dateTo }, token)
      setData(response)
    } catch (err) {
      setData(null)
      setError(
        err instanceof Error
          ? err.message
          : 'Nao foi possivel carregar as vendas da campanha.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [campaignId, enabled, page, perPage, dateFrom, dateTo, token])

  useEffect(() => {
    void load()
  }, [load])

  return {
    data,
    isLoading,
    error,
    refetch: load,
  }
}
